/**
 * HotelContext
 * ------------
 * Reads the :hotelSlug from the URL (e.g. /hotel/shreyas/...) and makes it
 * available throughout the app via useHotel().
 *
 * The apiClient interceptor uses this to automatically attach x-hotel-slug
 * to every API request so controllers can scope data to the right hotel.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

const HotelContext = createContext({
  hotelSlug: null,
  hotelBasePath: '/'
});

export function HotelProvider({ children }) {
  const params = useParams();
  const location = useLocation();

  // Extract hotelSlug either from route params or from the URL path directly
  const hotelSlug = useMemo(() => {
    let slug = params.hotelSlug;
    if (!slug) {
      // Fallback: parse from pathname e.g. /hotel/shreyas/admin
      const match = location.pathname.match(/^\/hotel\/([^/]+)/);
      slug = match ? match[1] : null;
    }
    if (slug) {
      localStorage.setItem('lastHotelSlug', slug);
    }
    return slug;
  }, [params.hotelSlug, location.pathname]);

  const hotelBasePath = hotelSlug ? `/hotel/${hotelSlug}` : '';

  const value = useMemo(() => ({ hotelSlug, hotelBasePath }), [hotelSlug, hotelBasePath]);

  return (
    <HotelContext.Provider value={value}>
      {children}
    </HotelContext.Provider>
  );
}

export function useHotel() {
  return useContext(HotelContext);
}

export default HotelContext;
